// src/pages/DraftPosts.js
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useStore } from '../store';
import { postsAPI } from '../utils/api';
import { getPlatform } from '../utils/platforms';
import { format } from 'date-fns';

export default function DraftPosts() {
  const navigate = useNavigate();
  const { posts, postsTotal, postsLoading, fetchPosts, deletePost, fetchSummary } = useStore();
  const [search, setSearch] = useState('');
  const [scheduleModal, setScheduleModal] = useState(null); // post to schedule
  const [schedDate, setSchedDate] = useState('');
  const [schedTime, setSchedTime] = useState('09:00');
  const [scheduling, setScheduling] = useState(false);

  useEffect(() => { fetchPosts({ status: 'draft' }); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this draft?')) return;
    await deletePost(id);
    toast.success('Draft deleted');
  };

  const openSchedule = (post) => {
    setScheduleModal(post);
    setSchedDate('');
    setSchedTime('09:00');
  };

  const handleSchedule = async () => {
    if (!schedDate) return toast.error('Please select a date');
    setScheduling(true);
    try {
      const scheduled_at = `${schedDate}T${schedTime}:00`;
      await postsAPI.update(scheduleModal.id, {
        caption: scheduleModal.caption,
        scheduled_at,
        status: 'queued',
        tags: scheduleModal.tags || [],
        media_ids: scheduleModal.media_ids || [],
      });
      toast.success('Post scheduled successfully');
      setScheduleModal(null);
      fetchPosts({ status: 'draft' });
      fetchSummary();
    } catch {
      toast.error('Failed to schedule post');
    } finally {
      setScheduling(false);
    }
  };

  const handlePublishNow = async (post) => {
    if (!window.confirm('Publish this post now?')) return;
    try {
      await postsAPI.update(post.id, {
        caption: post.caption,
        scheduled_at: new Date().toISOString(),
        status: 'queued',
        tags: post.tags || [],
        media_ids: post.media_ids || [],
      });
      toast.success('Post queued for publishing');
      fetchPosts({ status: 'draft' });
      fetchSummary();
    } catch {
      toast.error('Failed to publish post');
    }
  };

  const filtered = posts.filter(p => !search || p.caption?.toLowerCase().includes(search.toLowerCase()));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div className="page-header">
        <div>
          <span className="page-title">Draft Posts</span>
          <div className="page-subtitle">Unscheduled posts saved as drafts</div>
        </div>
        <button className="btn btn-primary" onClick={() => navigate('/posts/create')}>+ Create Post</button>
      </div>

      <div className="toolbar">
        <div className="search-box">
          <span style={{ color: '#aaa' }}>🔍</span>
          <input placeholder="Search drafts…" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <span className="count-text ml-auto">{filtered.length} of {postsTotal} drafts</span>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px' }}>
        {postsLoading ? (
          <div className="loading-center"><div className="spinner" /></div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <span style={{ fontSize: 40 }}>📝</span>
            <h3>No drafts yet</h3>
            <p>Posts saved as drafts will appear here</p>
            <button className="btn btn-primary" onClick={() => navigate('/posts/create')}>Create Post</button>
          </div>
        ) : (
          filtered.map(p => {
            const accData = p.accounts?.filter(a => a && a.platform) || [];
            return (
              <div key={p.id} style={{ background: '#fff', border: '1px solid #e8eaf0', borderRadius: 8, display: 'flex', marginBottom: 8, overflow: 'hidden' }}>
                <div style={{ width: 72, minHeight: 72, background: '#f0fdfa', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 22, color: '#c0c7d8' }}>📝</div>
                <div style={{ flex: 1, padding: '9px 12px', minWidth: 0 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#0f766e', marginBottom: 2, textTransform: 'uppercase', letterSpacing: '.05em' }}>Draft</div>
                  <div style={{ fontSize: 12, color: '#5a6578', lineHeight: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    {p.caption || <span style={{ color: '#aaa', fontStyle: 'italic' }}>No caption</span>}
                  </div>
                  {p.tags?.length > 0 && <div style={{ fontSize: 10, color: '#0f766e', marginTop: 2 }}>{p.tags.join(' ')}</div>}
                  <div style={{ fontSize: 10, color: '#aaa', marginTop: 4 }}>
                    Created {format(new Date(p.created_at), 'MMM d, yyyy')}
                  </div>
                </div>
                <div style={{ padding: '9px 12px', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, minWidth: 170, borderLeft: '1px solid #f0f1f5' }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'flex-end' }}>
                    {accData.slice(0, 3).map((a, i) => { const pl = getPlatform(a.platform); return <div key={i} className="platform-icon" style={{ background: pl.bg, color: pl.color, width: 18, height: 18 }}>{pl.icon}</div>; })}
                    {accData.length === 0 && <span style={{ fontSize: 10, color: '#aaa' }}>No accounts</span>}
                  </div>
                  <span className="badge badge-draft">Draft</span>
                  <div style={{ display: 'flex', gap: 5, marginTop: 3, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    <button className="btn btn-primary" style={{ padding: '3px 8px', fontSize: 10 }} onClick={() => openSchedule(p)}>📅 Schedule</button>
                    <button className="btn btn-outline" style={{ padding: '3px 8px', fontSize: 10 }} onClick={() => handlePublishNow(p)}>⚡ Now</button>
                    <button className="btn btn-outline" style={{ padding: '3px 8px', fontSize: 10 }} onClick={() => navigate(`/posts/edit/${p.id}`)}>✏ Edit</button>
                    <button className="btn btn-danger" style={{ padding: '3px 8px', fontSize: 10 }} onClick={() => handleDelete(p.id)}>🗑</button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {scheduleModal && (
        <div className="modal-overlay" onClick={() => setScheduleModal(null)}>
          <div className="modal" style={{ width: 360 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">Schedule Post</span>
              <button className="btn btn-ghost" style={{ padding: 4 }} onClick={() => setScheduleModal(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{ fontSize: 12, color: '#5a6578', marginBottom: 14, lineHeight: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                {scheduleModal.caption || 'No caption'}
              </div>
              <div className="form-group">
                <label className="form-label">Date <span className="req">*</span></label>
                <input type="date" className="form-input" value={schedDate} onChange={e => setSchedDate(e.target.value)} min={new Date().toISOString().slice(0, 10)} />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Time</label>
                <input type="time" className="form-input" value={schedTime} onChange={e => setSchedTime(e.target.value)} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setScheduleModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSchedule} disabled={scheduling}>
                {scheduling ? 'Scheduling…' : 'Schedule'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
